import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PublicContactStrip from "@/components/PublicContactStrip";

function LegalLayout({ title, effective, children, testid }) {
  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid={testid}>
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-900 mb-6"
          data-testid="legal-back-home"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-950">
          {title}
        </h1>
        <p className="mt-2 text-sm text-slate-500">{effective}</p>
        <div className="blog-content max-w-none mt-8 space-y-4">
          {children}
        </div>
        <hr className="my-10 border-slate-200" />
        <div className="text-xs text-slate-600 space-y-1">
          <p className="font-semibold text-slate-900">Central Rentals Canada Inc.</p>
          <p>
            Privacy Officer · {" "}
            <a href="mailto:privacy@centralrentals.ca" className="text-emerald-700 hover:underline">
              privacy@centralrentals.ca
            </a>
          </p>
          <p>
            General inquiries · {" "}
            <a href="mailto:contact@centralrentals.ca" className="text-emerald-700 hover:underline">
              contact@centralrentals.ca
            </a>
            {" · "}
            <a href="tel:+17804052498" className="text-emerald-700 hover:underline">
              780-405-2498
            </a>
          </p>
        </div>
      </div>
      <PublicContactStrip testid={`${testid}-contact`} />
    </div>
  );
}

export function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      effective="Central Rentals Canada · PIPEDA-compliant · Effective 2026-06-01"
      testid="page-privacy"
    >
      <h2>1. Who we are</h2>
      <p>
        Central Rentals Canada Inc. ("Central Rentals", "we", "us", "our")
        operates the Central Rentals Canada mobile application and
        centralrentals.ca (the "Service"). We are a Canadian property-management
        platform. This Privacy Policy
        explains what personal information we collect, why we collect it, how
        we use and share it, and the rights you have under Canadian privacy law
        (PIPEDA) and applicable provincial privacy legislation.
      </p>

      <h2>2. Information we collect</h2>
      <p>
        (a) Account data — name, email, phone number, password hash (bcrypt),
        role. (b) Profile data — photo, mailing address, language preference.
        (c) Property and lease data — property addresses, unit details, lease
        documents, rent amounts, deposit amounts, tenant names. (d) Payment
        data — Stripe customer ID, last 4 digits of card; full card numbers are
        processed by Stripe and never stored on our servers. (e) Maintenance
        data — text descriptions, photos and videos. (f) Tenant screening data
        (with explicit consent) — DOB, employer, references, credit/background
        check results. (g) Device & usage data — IP, device model, OS, app
        version, language, timezone, app interaction events, push tokens.
      </p>

      <h2>3. Why we collect it (purposes)</h2>
      <p>
        To provide the Service (accounts, listings, rent collection,
        maintenance, lease generation); authenticate you; fulfill legal
        obligations (tax filings such as T776, anti-fraud, AML); protect users
        (audit logs, access control); communicate with you; and improve the
        Service (aggregated analytics). We do not sell personal information.
      </p>

      <h2>4. Lawful basis under PIPEDA</h2>
      <p>
        Under PIPEDA and provincial equivalents (Alberta PIPA, BC PIPA, Quebec
        Law 25), we rely on your meaningful consent. By creating an account and
        continuing to use the Service, you consent to the collection, use and
        disclosure of personal information for the purposes described in this
        policy. You may withdraw consent at any time, subject to legal or
        contractual restrictions and reasonable notice, by contacting
        privacy@centralrentals.ca.
      </p>

      <h2>5. Service providers and third parties</h2>
      <p>
        We share the minimum personal information necessary with carefully
        selected service providers acting on our behalf: Stripe Inc.
        (payments), Google LLC (push notifications
        via FCM, Maps, optional Google Business Profile integration), and
        Canadian background-check providers when you explicitly request tenant
        screening. All providers are bound by contracts requiring
        confidentiality and use of data only for the purposes we specify. We do
        not sell, rent or trade your personal information to advertisers.
      </p>

      <h2>6. Cross-border transfers</h2>
      <p>
        Some service providers store or process data outside Canada (primarily
        the US). When personal information is transferred outside Canada, it
        remains subject to the laws of the receiving jurisdiction, which may
        differ from Canadian law. We take contractual and technical safeguards
        (encryption in transit, data-processing agreements, vendor
        due-diligence). For information about specific transfers, contact
        privacy@centralrentals.ca.
      </p>

      <h2>7. Security</h2>
      <p>
        Industry-standard technical and organizational measures: TLS 1.2+ for
        all data in transit; bcrypt password hashing; least-privilege access
        controls; encrypted database storage; audit logging on sensitive
        actions; periodic security reviews. If you believe your account has
        been compromised, contact privacy@centralrentals.ca immediately.
      </p>

      <h2>8. Retention</h2>
      <p>
        We retain personal information for as long as your account is active
        and as required to fulfil the purposes in this policy or to comply with
        our legal obligations (e.g. seven years for financial records under
        Canadian tax law). When personal information is no longer required, we
        delete or de-identify it.
      </p>

      <h2>9. Your rights</h2>
      <p>
        Under PIPEDA you have the right to: (a) access the personal information
        we hold about you; (b) request correction; (c) withdraw consent
        (subject to limits); (d) request deletion, subject to retention
        obligations; (e) lodge a complaint with the Office of the Privacy
        Commissioner of Canada (priv.gc.ca). To exercise these rights email
        privacy@centralrentals.ca. We will respond within 30 days.
      </p>

      <h2>10. Children</h2>
      <p>
        The Service is not directed to children under 16. We do not knowingly
        collect personal information from children under 16.
      </p>

      <h2>11. Cookies and tracking</h2>
      <p>
        On centralrentals.ca we use strictly-necessary cookies (session, CSRF,
        language) and optional analytics/preference cookies. You will be asked
        to consent before any non-essential cookies are set. In the mobile app
        we do not use third-party advertising or tracking SDKs.
      </p>

      <h2>12. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be
        notified in-app and by email at least 14 days before they take effect.
      </p>

      <h2>13. Contact us</h2>
      <p>
        Contact our Privacy Officer at privacy@centralrentals.ca.
      </p>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      effective="Central Rentals Canada · Effective 2026-06-01"
      testid="page-terms"
    >
      <h2>1. Acceptance of Terms</h2>
      <p>
        By creating an account or using the Central Rentals Canada mobile
        application or centralrentals.ca (collectively, the "Service"), you
        agree to be bound by these Terms of Service and our Privacy Policy. If
        you do not agree, do not use the Service.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years of age and legally able to enter into
        binding contracts under the laws of your province or territory.
      </p>

      <h2>3. Accounts and security</h2>
      <p>
        You are responsible for maintaining the confidentiality of your account
        credentials and for all activities under your account. Notify us
        immediately at privacy@centralrentals.ca if you suspect unauthorized
        access.
      </p>

      <h2>4. Subscriptions and billing</h2>
      <p>
        Landlord features are offered on a recurring subscription billed
        monthly or annually. All charges are processed by Stripe. Subscriptions
        auto-renew until cancelled. You may cancel at any time from Billing →
        Manage Subscription. Tenant rent payments are processed at your
        election and remitted to the landlord less applicable fees.
      </p>

      <h2>5. Property management content</h2>
      <p>
        You retain ownership of content you upload (lease documents, photos,
        marketing copy). By uploading, you grant Central Rentals a worldwide,
        royalty-free licence to host, display and process that content solely
        to provide the Service.
      </p>

      <h2>6. Acceptable use</h2>
      <p>
        You will not use the Service to violate any law (including
        landlord-tenant, fair-housing or anti-discrimination laws); upload
        malicious code; reverse-engineer the Service; or harvest data about
        other users.
      </p>

      <h2>7. Third-party services</h2>
      <p>
        The Service integrates with third-party services (Stripe, Apple,
        Google, background-check providers). Your use of those services is also
        governed by their respective terms and privacy policies.
      </p>

      <h2>8. Termination</h2>
      <p>
        We may suspend or terminate your account for breach of these Terms,
        fraud, or as required by law. You may delete your account at any time.
      </p>

      <h2>9. Disclaimer of warranties</h2>
      <p>
        The Service is provided "as is" and "as available" without warranties
        of any kind, express or implied, to the maximum extent permitted by
        law.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Central Rentals' aggregate
        liability arising out of or related to the Service will not exceed the
        greater of (i) CA$100 or (ii) the amount paid by you to Central Rentals
        in the 12 months immediately preceding the event giving rise to the
        claim.
      </p>

      <h2>11. Governing law</h2>
      <p>
        These Terms are governed by the laws of the Province of Alberta and the
        federal laws of Canada applicable therein. The courts of Alberta have
        exclusive jurisdiction.
      </p>

      <h2>12. Changes to Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be
        notified in-app and by email at least 14 days before they take effect.
      </p>

      <h2>13. Contact us</h2>
      <p>
        Email privacy@centralrentals.ca.
      </p>
    </LegalLayout>
  );
}

export function ScreeningDisclosure() {
  return (
    <LegalLayout
      title="Tenant Screening Consent & Disclosure"
      effective="Central Rentals Canada · PIPEDA + provincial consumer-reporting compliant · Effective 2026-06-01"
      testid="page-legal-screening"
    >
      <h2>1. What this document covers</h2>
      <p>
        This disclosure explains what personal information Central Rentals
        Canada collects during a tenant screening, why we collect it, who we
        share it with, and the rights you have as an applicant under the
        federal <em>Personal Information Protection and Electronic Documents
        Act</em> (PIPEDA) and applicable provincial consumer-reporting
        legislation (e.g. Ontario <em>Consumer Reporting Act</em>, Alberta
        <em>Fair Trading Act</em>, BC <em>Business Practices and Consumer
        Protection Act</em>, Quebec Law 25).
      </p>

      <h2>2. Information collected with your consent</h2>
      <p>
        (a) Identity — full legal name, date of birth, government-issued ID
        number (where required by law). (b) Contact — email, phone, current
        address. (c) Employment — employer name, position, income
        verification. (d) Rental history — prior addresses, landlord
        references. (e) Credit information — credit score, tradelines,
        collections, bankruptcies, from a Canadian consumer-reporting agency
        (Equifax or TransUnion Canada). (f) Public-record information —
        court judgments, evictions, where lawfully accessible.
      </p>

      <h2>3. Purpose</h2>
      <p>
        We collect the information above <strong>only</strong> to help the
        landlord decide whether to offer you a tenancy and, if required by
        law, to establish reasonable grounds under the applicable Residential
        Tenancies Act. We do not use screening data for advertising,
        profiling, or resale.
      </p>

      <h2>4. Your consent</h2>
      <p>
        Screening only proceeds after you click a one-time secure consent
        link and confirm your identity. You may refuse or withdraw consent at
        any time, subject to reasonable notice, by emailing
        privacy@centralrentals.ca. Withdrawal may mean the landlord cannot
        continue processing your application.
      </p>

      <h2>5. Adverse-action rights</h2>
      <p>
        If a decision is made based even in part on the screening report, you
        have the right to (a) be told which consumer-reporting agency
        produced the report, (b) receive a free copy of that report from the
        agency, and (c) dispute any inaccuracies directly with the agency.
        We will provide the agency&rsquo;s name and contact details within 30
        days of your written request.
      </p>

      <h2>6. Data-handling and retention</h2>
      <p>
        Screening data is transmitted over TLS 1.2+, stored encrypted at
        rest, and accessible only to the requesting landlord and to Central
        Rentals staff with a documented business need. We retain screening
        reports for the shorter of (i) the length of the resulting tenancy
        plus seven years, or (ii) the maximum retention period allowed by
        the applicable provincial consumer-reporting statute.
      </p>

      <h2>7. Third parties</h2>
      <p>
        We share the minimum information necessary with our Canadian
        consumer-reporting partners solely to produce the screening report.
        These providers are contractually bound to use the data only for that
        purpose and to comply with PIPEDA and their governing provincial
        legislation. We do not sell, rent, or trade screening data.
      </p>

      <h2>8. Your rights</h2>
      <p>
        You have the right to (a) access the personal information we hold
        about you; (b) request correction; (c) withdraw consent (subject to
        limits); (d) request deletion, subject to legal retention
        obligations; and (e) lodge a complaint with the Office of the
        Privacy Commissioner of Canada (priv.gc.ca) or your provincial
        privacy commissioner. Email privacy@centralrentals.ca to exercise
        these rights &mdash; we respond within 30 days.
      </p>

      <h2>9. Fair housing</h2>
      <p>
        Central Rentals follows the <Link to="/equal-housing" className="text-emerald-700 underline">Equal Housing</Link> commitment. Screening
        decisions may never be based on race, ancestry, place of origin,
        colour, ethnic origin, citizenship, creed, sex, sexual orientation,
        gender identity, age, marital status, family status, disability, or
        receipt of public assistance.
      </p>

      <h2>10. Contact</h2>
      <p>
        Privacy Officer &mdash; privacy@centralrentals.ca &middot; Central
        Rentals Canada Inc.
      </p>
    </LegalLayout>
  );
}
